import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import RecommendationsEditor from "@/pages/assessment/RecommendationsEditor";

const LEVEL_BG = {
  "Crítico": "bg-rose-600 text-white",
  "Crítica": "bg-rose-600 text-white",  // legacy AIHA label
  "Alto": "bg-orange-500 text-white",
  "Moderado": "bg-amber-500 text-white",
  "Baixo": "bg-emerald-500 text-white",
  "Tolerável": "bg-emerald-500 text-white",
  "Aceitável": "bg-emerald-600 text-white",
  "Intolerável": "bg-rose-700 text-white",
};
const HORIZONTE_BG = {
  "Imediato (30 dias)": "bg-rose-100 text-rose-800",
  "Curto (90 dias)": "bg-orange-100 text-orange-800",
  "Médio (6 meses)": "bg-amber-100 text-amber-800",
  "Longo (12 meses)": "bg-emerald-100 text-emerald-800",
};
const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
};

export default function AssessmentAIPanel({ assessmentId, responseCount }) {
  const [history, setHistory] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const r = await api.get(`/assessments/${assessmentId}/ai-analyses`);
      setHistory(r.data || []);
      if (r.data && r.data.length > 0 && !current) {
        const latest = await api.get(`/assessments/${assessmentId}/ai-analyses/${r.data[0].id}`);
        setCurrent(latest.data);
      }
    } catch (e) { console.error(e); }
    finally { setHistoryLoading(false); }
  }, [assessmentId, current]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const generate = async (force = false) => {
    if (responseCount === 0) {
      toast.error("É preciso ter ao menos uma resposta para gerar a análise.");
      return;
    }
    setLoading(true);
    const t = toast.loading(force ? "Iniciando regeneração…" : "Iniciando análise com Claude Sonnet 4.6…");
    try {
      const r = await api.post(
        `/assessments/${assessmentId}/ai-analysis${force ? "?force=true" : ""}`,
        {},
      );
      if (r.data.status === "done" || r.data.analysis) {
        setCurrent(r.data);
        toast.dismiss(t);
        toast.success(r.data.cached ? "Análise carregada do cache." : "Análise gerada!");
        loadHistory(); setLoading(false); return;
      }
      const jobId = r.data.job_id;
      if (!jobId) throw new Error("Resposta inesperada do servidor.");
      toast.dismiss(t);
      const pollToast = toast.loading("Analisando dados com IA (Claude Sonnet 4.6) — costuma levar 60-120s. Pode deixar a janela aberta.");
      const maxAttempts = 60;
      let finalJob = null;
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise((res) => setTimeout(res, 3000));
        try {
          const j = await api.get(`/assessments/ai-jobs/${jobId}`);
          if (j.data.status === "done") { finalJob = j.data; break; }
          if (j.data.status === "error") {
            toast.dismiss(pollToast);
            toast.error(`A IA falhou: ${j.data.error || "erro desconhecido"}`);
            setLoading(false); return;
          }
        } catch (_e) { /* transient — keep polling */ }
      }
      toast.dismiss(pollToast);
      if (!finalJob) { toast.error("Tempo limite excedido. Tente novamente em alguns instantes."); setLoading(false); return; }
      const aid = finalJob.analysis_id;
      const a = await api.get(`/assessments/${assessmentId}/ai-analyses/${aid}`);
      setCurrent(a.data);
      toast.success("Análise gerada com sucesso!");
      loadHistory();
    } catch (e) {
      toast.dismiss(t);
      toast.error(e.response?.data?.detail || e.message || "Erro ao gerar análise");
    } finally { setLoading(false); }
  };

  const loadAnalysis = async (aid) => {
    try {
      const r = await api.get(`/assessments/${assessmentId}/ai-analyses/${aid}`);
      setCurrent(r.data); setShowHistory(false);
      window.scrollTo({ top: document.getElementById("ai-section")?.offsetTop || 0, behavior: "smooth" });
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao carregar análise"); }
  };

  const deleteAnalysis = async (aid) => {
    if (!window.confirm("Excluir esta análise do histórico?")) return;
    try {
      await api.delete(`/assessments/${assessmentId}/ai-analyses/${aid}`);
      toast.success("Análise excluída.");
      if (current?.id === aid) setCurrent(null);
      loadHistory();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao excluir"); }
  };

  const a = current?.analysis;

  return (
    <section id="ai-section" className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mt-6" data-testid="assessment-ai-section">
      <header className="px-6 py-5 border-b border-slate-100 bg-gradient-to-br from-indigo-50/50 to-white">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 text-white shadow-md">
              <i className="fa-solid fa-wand-magic-sparkles text-lg"></i>
            </span>
            <div>
              <h2 className="font-bold text-slate-900 text-lg">Análise interpretativa por IA</h2>
              <p className="text-xs text-slate-500">Relatório técnico narrativo gerado por Claude Sonnet 4.6 — estilo consultoria sênior em SST e Psicologia Organizacional.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {history.length > 0 && (
              <button onClick={() => setShowHistory(!showHistory)} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid="btn-toggle-history">
                <i className="fa-solid fa-clock-rotate-left mr-1"></i>Histórico ({history.length})
              </button>
            )}
            {current && (
              <a href={`/teste-pdf-aval/${assessmentId}/${current.id}`} target="_blank" rel="noreferrer"
                 className="rounded-lg border border-emerald-300 bg-white text-emerald-700 font-semibold px-3 py-2 text-sm hover:bg-emerald-50" data-testid="btn-export-pdf">
                <i className="fa-solid fa-file-pdf mr-1"></i> PDF Premium
              </a>
            )}
            {current ? (
              <button onClick={() => generate(true)} disabled={loading}
                      className="rounded-lg bg-indigo-600 text-white font-semibold px-4 py-2 text-sm hover:bg-indigo-700 disabled:opacity-50" data-testid="btn-regenerate-ai">
                {loading ? <><i className="fa-solid fa-spinner fa-spin mr-1"></i>Regenerando…</> : <><i className="fa-solid fa-rotate mr-1"></i>Regenerar</>}
              </button>
            ) : (
              <button onClick={() => generate(false)} disabled={loading || responseCount === 0}
                      className="rounded-lg bg-gradient-to-r from-indigo-500 to-indigo-700 text-white font-semibold px-4 py-2 text-sm hover:shadow-lg disabled:opacity-50" data-testid="btn-generate-ai">
                {loading ? <><i className="fa-solid fa-spinner fa-spin mr-1"></i>Gerando…</> : <><i className="fa-solid fa-wand-magic-sparkles mr-1"></i>Gerar análise por IA</>}
              </button>
            )}
          </div>
        </div>
        {showHistory && (
          <div className="mt-4 bg-white border border-slate-200 rounded-xl p-3 max-h-72 overflow-y-auto" data-testid="ai-history-list">
            {historyLoading ? <div className="text-xs text-slate-400 p-2">Carregando histórico…</div>
             : history.length === 0 ? <div className="text-xs text-slate-400 p-2">Nenhuma análise no histórico.</div>
             : (
              <ul className="divide-y divide-slate-100">
                {history.map((h) => (
                  <li key={h.id} className={`py-2 px-2 flex items-center gap-3 hover:bg-slate-50 rounded ${current?.id === h.id ? "bg-indigo-50" : ""}`}>
                    <button onClick={() => loadAnalysis(h.id)} className="flex-1 text-left">
                      <div className="text-sm font-semibold text-slate-800">{fmtDate(h.created_at)}</div>
                      <div className="text-xs text-slate-500">
                        {h.response_count_at_generation} resp. · score {h.overall_score?.toFixed?.(2) || h.overall_score} · {h.overall_level} · por {h.generated_by}
                      </div>
                    </button>
                    <button onClick={() => deleteAnalysis(h.id)} className="text-rose-500 hover:text-rose-700 p-1" aria-label="Excluir"><i className="fa-solid fa-trash text-xs"></i></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </header>

      {!a ? (
        <div className="p-10 text-center">
          {responseCount === 0 ? (
            <>
              <i className="fa-solid fa-circle-info text-3xl text-slate-300 block mb-3"></i>
              <p className="text-sm text-slate-500">A análise por IA estará disponível assim que houver pelo menos uma resposta.</p>
            </>
          ) : (
            <>
              <i className="fa-solid fa-wand-magic-sparkles text-3xl text-indigo-300 block mb-3"></i>
              <p className="text-sm text-slate-600 max-w-md mx-auto">
                Clique em <b>&quot;Gerar análise por IA&quot;</b> para um relatório técnico narrativo baseado em {responseCount} resposta{responseCount > 1 ? "s" : ""}.
              </p>
              <p className="text-xs text-slate-400 mt-2">Custo estimado: ~R$ 0,15 · Tempo: 60-120s</p>
            </>
          )}
        </div>
      ) : (
        <div className="p-6 md:p-8 space-y-7" data-testid="ai-analysis-body">
          <article>
            <h3 className="text-sm font-bold uppercase tracking-wider text-indigo-700 mb-2"><i className="fa-solid fa-bullseye mr-2"></i>Resumo executivo</h3>
            <p className="text-slate-800 leading-relaxed bg-indigo-50/40 border-l-4 border-indigo-500 p-4 rounded-r-lg">{a.resumo_executivo}</p>
          </article>
          <article>
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2"><i className="fa-solid fa-building-circle-check mr-2"></i>Perfil geral da organização</h3>
            <div className="text-slate-700 leading-relaxed whitespace-pre-line">{a.perfil_organizacao}</div>
          </article>
          {a.fatores_criticos?.length > 0 && (
            <article>
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-3"><i className="fa-solid fa-triangle-exclamation mr-2 text-rose-500"></i>Principais fatores críticos</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {a.fatores_criticos.map((f, i) => (
                  <div key={i} className="border border-slate-200 rounded-xl p-4 bg-white shadow-sm">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-bold text-slate-900">{f.dimensao}</h4>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded ${LEVEL_BG[f.nivel] || "bg-slate-300 text-slate-700"}`}>{f.nivel}</span>
                    </div>
                    <div className="text-xs text-slate-500 mb-1">Média {f.media?.toFixed?.(2) ?? f.media}</div>
                    <p className="text-sm text-slate-700">{f.leitura}</p>
                  </div>
                ))}
              </div>
            </article>
          )}
          {a.interpretacao_dimensoes?.length > 0 && (
            <article>
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-3"><i className="fa-solid fa-chart-line mr-2"></i>Interpretação por dimensão</h3>
              <div className="space-y-3">
                {a.interpretacao_dimensoes.map((d, i) => (
                  <div key={i} className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-bold text-slate-800 text-sm">{d.dimensao}</h4>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="font-mono font-bold text-slate-900">{d.media?.toFixed?.(2) ?? d.media}</span>
                        <span className={`font-bold px-2 py-0.5 rounded ${LEVEL_BG[d.nivel] || "bg-slate-300 text-slate-700"}`}>{d.nivel}</span>
                      </div>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed">{d.interpretacao}</p>
                  </div>
                ))}
              </div>
            </article>
          )}
          <article>
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2"><i className="fa-solid fa-ranking-star mr-2 text-amber-500"></i>Ranking dos riscos</h3>
            <p className="text-slate-700 leading-relaxed">{a.ranking_riscos}</p>
          </article>
          <article>
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2"><i className="fa-solid fa-diagram-project mr-2 text-indigo-500"></i>Relação entre fatores</h3>
            <div className="text-slate-700 leading-relaxed whitespace-pre-line">{a.relacao_fatores}</div>
          </article>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {a.causas_organizacionais?.length > 0 && (
              <article>
                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2"><i className="fa-solid fa-magnifying-glass mr-2 text-amber-600"></i>Possíveis causas organizacionais</h3>
                <ul className="list-disc list-inside space-y-1.5 text-sm text-slate-700">{a.causas_organizacionais.map((c, i) => <li key={i}>{c}</li>)}</ul>
              </article>
            )}
            {a.consequencias_potenciais?.length > 0 && (
              <article>
                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2"><i className="fa-solid fa-circle-exclamation mr-2 text-rose-600"></i>Consequências potenciais</h3>
                <ul className="list-disc list-inside space-y-1.5 text-sm text-slate-700">{a.consequencias_potenciais.map((c, i) => <li key={i}>{c}</li>)}</ul>
              </article>
            )}
          </div>
          <RecommendationsEditor
            items={a.recomendacoes_prioritarias || []}
            patchEndpoint={`/assessments/${assessmentId}/ai-analyses/${current.id}/recommendations`}
            onSaved={(updated) => setCurrent(updated)}
          />
          <article>
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2"><i className="fa-solid fa-flag-checkered mr-2 text-slate-500"></i>Conclusão técnica</h3>
            <div className="text-slate-700 leading-relaxed whitespace-pre-line bg-slate-50 p-4 rounded-lg border border-slate-200">{a.conclusao_tecnica}</div>
          </article>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-xs text-amber-900">
            <i className="fa-solid fa-shield-halved mr-2"></i><b>Aviso:</b> {a.disclaimer}
          </div>
          <div className="text-xs text-slate-400 text-center pt-2 border-t border-slate-100">
            Análise gerada em {fmtDate(current.created_at)} · modelo {current.model} · {current.response_count_at_generation} resp.
          </div>
        </div>
      )}
    </section>
  );
}
